import { EndSessionMessage } from "@messages/session";
import { Timer, Vector3 } from "three";
import { camera } from "../rendering/camera";
import { clockService } from "../xr/ClockService";
import { renderManager } from "../xr/RenderManager";
import { animationFrame$ } from "@messages/xr";
import { startup } from "./startup";
import { Module } from "../module";
import { CameraUpdateMessage, FrameStats, FrameUpdateMessage } from "@messages/rendering";
import { ROOT_SCALE } from "../rendering/rootScale";
import { localPlayerContainer } from "../localplayer/LocalPlayerContainer";
import { ModuleId, AskMessage, RequestFrameStatsMessage, FrameStatsMessage, SaveDataFileMessage, hrefHasParameter, checkQuaternionNear, checkVector3Near, checkScalarNear, } from "flavr-common";
import { renderer } from "../rendering/RendererGlobals";
import { busServiceMain } from "../bus";
import { sessionManagerService } from "../multiplayer/session/SessionManagerService";
import { cacheService } from "../cache";
import { threeJsCachingService } from "../web";
import { now } from "@messages/timing";
import { mainScene } from "../rendering/mainScene";
let paused = false;
const PAUSE_ON_FOCUS_LOST = hrefHasParameter(window.location.href, "pause");
const START_POSITION = new Vector3(0, 0, 0).multiplyScalar(ROOT_SCALE);
const START_CAMERA = new Vector3(0, 0, 0).multiplyScalar(ROOT_SCALE);
if (hrefHasParameter(window.location.href, "operator")) {
    START_POSITION.set(2.28, 0, 0.36);
}
// Enable FileLoader caching
threeJsCachingService.enabled = true;
cacheService.enabled = true;
/**
 * Boots the main client view and drives the frame loop that wires input, movement, rendering and camera synchronization together.
 *
 * Messages:
 * - `RequestFrameStatsMessage` - Answers profiling and frame-statistics requests.
 * - `SaveDataFileMessage` - Downloads generated data to a local file.
 * - `EndSessionMessage` - Restores the local view to its default state.
 */
export class ViewInitModule extends Module {
    /**
     * Positions the camera and local player at their start transform, starts the render clock, initializes the
     * render manager and app startup sequence, kicks off the render loop via `tick`, and subscribes to frame-stats
     * requests, session-end and save-file messages.
     */
    constructor() {
        super(ModuleId.VIEW_INIT);
        this.clock = new Timer();
        this.lastCameraPosition = [0, 0, 0];
        this.lastCameraOrientation = [0, 0, 0, 1];
        this.lastViewDirection = [0, 0, 0];
        this.lastPlatformScale = 1;
        this.detailedStats = {
            onInputUpdate: new FrameStats(),
            onMovementUpdate: new FrameStats(),
            onUpdate: new FrameStats(),
            onPreRenderSubject: new FrameStats(),
            onRenderSubject: new FrameStats(),
            onLateUpdateSubject: new FrameStats(),
            count: 0
        };
        /// Private life cycle methods ///
        this.tick = (_time, xrFrame /* XRFrame */) => {
            animationFrame$.frameStats.begin();
            this.clock.update();
            clockService.lastUpdateTime = now();
            this.detailedStats.onInputUpdate.begin();
            animationFrame$.onInputUpdate$.invoke(xrFrame);
            this.detailedStats.onInputUpdate.end();
            this.detailedStats.onMovementUpdate.begin();
            animationFrame$.onMovementUpdate$.invoke();
            this.detailedStats.onMovementUpdate.end();
            clockService.lastUpdateTime = now();
            this.detailedStats.onUpdate.begin();
            animationFrame$.onUpdateStatsBeginDelegate.invoke();
            animationFrame$.invoke(++this.detailedStats.count);
            animationFrame$.onUpdateStatsEndDelegate.invoke();
            this.detailedStats.onUpdate.end();
            clockService.lastUpdateTime = now();
            this.detailedStats.onPreRenderSubject.begin();
            // tell modules ro update
            this.connection.send(new FrameUpdateMessage());
            animationFrame$.onPreRender$.invoke();
            this.detailedStats.onPreRenderSubject.end();
            this.detailedStats.onRenderSubject.begin();
            animationFrame$.onRender$.invoke();
            this.detailedStats.onRenderSubject.end();
            clockService.lastUpdateTime = now();
            this.detailedStats.onLateUpdateSubject.begin();
            animationFrame$.onLateUpdate$.invoke();
            this.detailedStats.onLateUpdateSubject.end();
            animationFrame$.frameStats.end();
        };
        // Add the camera
        camera.perspectiveCam.position.copy(START_POSITION);
        localPlayerContainer.init();
        busServiceMain.onWindowVisibleChanged(document.visibilityState !== undefined ? document.visibilityState === "visible" : true);
        document.addEventListener("visibilitychange", () => {
            busServiceMain.onWindowVisibleChanged(document.visibilityState === "visible");
        });
        renderManager.init();
        startup();
        this.initializeWindowActiveHandler();
        this.connection.subscribe([`${RequestFrameStatsMessage.typestatic}${ModuleId.VIEW_INIT}`, EndSessionMessage.typestatic, SaveDataFileMessage.typestatic], (msg) => {
            if (AskMessage.is(msg)) {
                if (RequestFrameStatsMessage.is(msg.message)) {
                    const frameStats = animationFrame$.frameStats;
                    this.connection.send(new FrameStatsMessage(msg.message.id, frameStats.getFPS(), frameStats.getFrameTime(), frameStats.getAvgTime(), frameStats.getWorstTime(), frameStats.getBestTime(), frameStats.getUsedMemory()), msg.askId);
                }
            }
            else if (SaveDataFileMessage.is(msg)) {
                const dataURI = "data:text/plain;base64," + btoa(msg.data);
                this.saveFile(dataURI, msg.fileName);
            }
            else if (EndSessionMessage.is(msg)) {
                this.onReset();
            }
        });
    }
    /**
     * Registers the per-frame pre-render callback and the optional window focus handling used by the main view loop.
     */
    init() {
        camera.perspectiveCam.updateMatrixWorld(true);
        mainScene.updateMatrixWorld(true);
        renderer.compile(mainScene, camera.perspectiveCam);
        renderer.setAnimationLoop(this.tick);
        animationFrame$.onPreRender$.add(() => {
            this.onPreRender();
        });
        if (PAUSE_ON_FOCUS_LOST) {
            window.addEventListener("focus", () => {
                this.resume();
            }, false);
            window.addEventListener("blur", () => {
                this.pause();
            }, false);
        }
    }
    /**
     * Resets the camera and local player transform after a session ends unless a broader session reset is already in progress.
     */
    onReset() {
        if (sessionManagerService.sessionIsResetting || sessionManagerService.wasSessionReset)
            return;
        camera.perspectiveCam.position.copy(START_POSITION);
        if (renderManager.isXRPresenting()) {
            localPlayerContainer.setPosition(START_POSITION);
        }
        else {
            camera.perspectiveCam.position.copy(START_CAMERA);
        }
    }
    onPreRender() {
        const elapsedTime = this.clock.getElapsed();
        // Send camera update if something changed
        const cameraWorldPosition = localPlayerContainer.cameraWorldPosition.toArray();
        const cameraWorldOrientation = localPlayerContainer.cameraWorldOrientation.toArray();
        const positionUnchanged = checkVector3Near(cameraWorldPosition, this.lastCameraPosition);
        const orientationUnchanged = checkQuaternionNear(cameraWorldOrientation, this.lastCameraOrientation);
        const directionUnchanged = checkVector3Near(renderManager.currentViewDirectionArray, this.lastViewDirection);
        const scaleUnchanged = checkScalarNear(localPlayerContainer.getPlatformScale(), this.lastPlatformScale);
        const transformUnchanged = positionUnchanged && orientationUnchanged && directionUnchanged && scaleUnchanged;
        if (!transformUnchanged) {
            this.connection.send(new CameraUpdateMessage(cameraWorldPosition, cameraWorldOrientation, renderManager.currentViewDirectionArray, localPlayerContainer.getPlatformScale()));
            this.lastCameraPosition[0] = cameraWorldPosition[0];
            this.lastCameraPosition[1] = cameraWorldPosition[1];
            this.lastCameraPosition[2] = cameraWorldPosition[2];
            this.lastCameraOrientation[0] = cameraWorldOrientation[0];
            this.lastCameraOrientation[1] = cameraWorldOrientation[1];
            this.lastCameraOrientation[2] = cameraWorldOrientation[2];
            this.lastCameraOrientation[3] = cameraWorldOrientation[3];
            this.lastViewDirection[0] = renderManager.currentViewDirectionArray[0];
            this.lastViewDirection[1] = renderManager.currentViewDirectionArray[1];
            this.lastViewDirection[2] = renderManager.currentViewDirectionArray[2];
            this.lastPlatformScale = Number(localPlayerContainer.getPlatformScale());
        }
    }
    /// Private helper for saving files ///
    saveFile(strData, filename) {
        const link = document.createElement("a");
        document.body.appendChild(link); // Firefox requires the link to be in the body
        link.download = filename;
        link.href = strData;
        link.click();
        document.body.removeChild(link); // remove the link when done
    }
    /// Private pausing and resuming of the render loop ///
    resume() {
        if (paused) {
            paused = false;
            console.log("[app] Window received focus. continue rendering...");
            clockService.resume();
            renderer.setAnimationLoop(this.tick);
        }
    }
    pause() {
        if (!paused) {
            paused = true;
            console.log("[app] Window lost focus. pause rendering...");
            clockService.pause();
            renderer.setAnimationLoop(() => { });
        }
    }
    /// Initialize ///
    initializeWindowActiveHandler() {
        const UPDATE_TIME_FOR_HIDDEN_WINDOW = 1000 / 25;
        let timeId;
        document.addEventListener("visibilitychange", () => {
            if (timeId !== undefined) {
                window.clearInterval(timeId);
            }
            if (document.visibilityState !== "visible") {
                timeId = window.setInterval(() => {
                    this.tick(0);
                }, UPDATE_TIME_FOR_HIDDEN_WINDOW);
            }
        });
    }
}
